import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

export default function Competitions() {
  const { clubId } = useParams();
  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/competitions').then(res => {
      setCompetitions(res.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadLeaderboard = async (compId) => {
    try {
      const [detail, lb] = await Promise.all([
        api.get(`/api/competitions/${compId}?clubId=${clubId}`),
        api.get(`/api/competitions/${compId}/leaderboard`),
      ]);
      setSelectedComp(detail.data);
      setLeaderboard(lb.data || []);
    } catch (err) {
      console.error('Failed to load competition:', err);
    }
  };

  const enterComp = async (compId) => {
    try {
      await api.post(`/api/competitions/${compId}/enter`, { clubId: parseInt(clubId) });
      loadLeaderboard(compId);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to enter');
    }
  };

  if (loading) return <p style={{ color: '#aaa' }}>Loading competitions...</p>;

  if (selectedComp) {
    return (
      <div>
        <button onClick={() => setSelectedComp(null)} style={{
          background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', marginBottom: 16
        }}>Back to Competitions</button>

        <div className="comp-detail-card" style={{ background: '#2a2d3a', borderRadius: 10, padding: 24, marginBottom: 20 }}>
          {selectedComp.bannerImage && <img src={selectedComp.bannerImage} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 16 }} />}
          <h2 style={{ color: '#fff', margin: '0 0 8px' }}>{selectedComp.title}</h2>
          <p style={{ color: '#aaa', margin: '0 0 16px' }}>{selectedComp.description}</p>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
            <div><span style={{ color: '#aaa', fontSize: 12 }}>State</span><br /><span style={{ color: '#fff' }}>{selectedComp.state}</span></div>
            <div><span style={{ color: '#aaa', fontSize: 12 }}>Period</span><br /><span style={{ color: '#fff' }}>{selectedComp.periodNum || '-'}</span></div>
            <div><span style={{ color: '#aaa', fontSize: 12 }}>Entrants</span><br /><span style={{ color: '#fff' }}>{selectedComp.entrantCount}{selectedComp.entrantLimit ? `/${selectedComp.entrantLimit}` : ''}</span></div>
            {selectedComp.entryFee && <div><span style={{ color: '#aaa', fontSize: 12 }}>Entry Fee</span><br /><span style={{ color: '#fff' }}>${selectedComp.entryFee}</span></div>}
          </div>

          {!selectedComp.isEntered && selectedComp.canUsersEnter && (
            <button onClick={() => enterComp(selectedComp.id)} style={{
              padding: '10px 24px', background: '#fbbf24', color: '#000', border: 'none',
              borderRadius: 6, fontWeight: 700, cursor: 'pointer'
            }}>Enter Competition</button>
          )}
          {selectedComp.isEntered && <span style={{ color: '#10b981', fontWeight: 600 }}>Entered</span>}
        </div>

        <h3 style={{ color: '#fff', marginBottom: 12 }}>Leaderboard</h3>
        {selectedComp.leaderboardBlurb && <p style={{ color: '#aaa', marginBottom: 16 }}>{selectedComp.leaderboardBlurb}</p>}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #3a3d4a' }}>
              {['#', 'Club', 'ROI', 'Wagered', 'Won', 'Bets', 'Members', 'Qualified'].map(h =>
                <th key={h} style={{ padding: '10px 8px', textAlign: 'left', color: '#aaa', fontSize: 12 }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((entry, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #2a2d3a', background: entry.clubId === parseInt(clubId) ? '#1e3a5f22' : 'transparent' }}>
                <td style={{ padding: '10px 8px', color: '#fff', fontWeight: 600 }}>
                  {entry.position}
                  {entry.previousPosition && entry.previousPosition !== entry.position && (
                    <span style={{ fontSize: 10, marginLeft: 4, color: entry.position < entry.previousPosition ? '#10b981' : '#ef4444' }}>
                      {entry.position < entry.previousPosition ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>{entry.clubName || `Club #${entry.clubId}`}</td>
                <td style={{ padding: '10px 8px', color: entry.roi >= 100 ? '#10b981' : '#ef4444', fontWeight: 600 }}>{entry.roi?.toFixed(1)}%</td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>${((entry.wagered || 0) / 100).toFixed(2)}</td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>${((entry.won || 0) / 100).toFixed(2)}</td>
                <td style={{ padding: '10px 8px', color: '#aaa' }}>{entry.numBets || 0}</td>
                <td style={{ padding: '10px 8px', color: '#aaa' }}>{entry.numMembers || 0}</td>
                <td style={{ padding: '10px 8px' }}>
                  {entry.qualified ? <span style={{ color: '#10b981' }}>Yes</span> : <span style={{ color: '#666' }}>No</span>}
                </td>
              </tr>
            ))}
            {leaderboard.length === 0 && <tr><td colSpan={8} style={{ padding: 20, color: '#666', textAlign: 'center' }}>No entries yet</td></tr>}
          </tbody>
        </table>

        {selectedComp.recentWinners?.length > 0 && (
          <>
            <h3 style={{ color: '#fff', margin: '24px 0 12px' }}>Recent Winners</h3>
            {selectedComp.recentWinners.map((w, i) => (
              <div key={i} className="comp-winner-card" style={{ background: '#2a2d3a', padding: 12, borderRadius: 6, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#fff' }}>Club #{w.clubId} - Period {w.periodNum}</span>
                <span style={{ color: '#fbbf24' }}>ROI: {w.roi?.toFixed(1)}%</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ color: '#fff', marginBottom: 20 }}>Competitions</h2>
      {competitions.length === 0 ? (
        <p style={{ color: '#aaa' }}>No active competitions at this time.</p>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {competitions.map(comp => (
            <div key={comp.id} onClick={() => loadLeaderboard(comp.id)} className="comp-list-card" style={{
              background: '#2a2d3a', borderRadius: 10, padding: 20, cursor: 'pointer',
              border: '1px solid #3a3d4a', transition: 'border-color 0.2s'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ color: '#fff', margin: '0 0 4px' }}>{comp.title}</h3>
                  <p style={{ color: '#aaa', margin: 0, fontSize: 14 }}>{comp.description?.slice(0, 120)}</p>
                </div>
                <span style={{
                  padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: comp.state === 'running' ? '#065f46' : '#92400e', color: '#fff'
                }}>{comp.state}</span>
              </div>
              {comp.startDate && (
                <p style={{ color: '#666', fontSize: 12, margin: '8px 0 0' }}>
                  {new Date(comp.startDate).toLocaleDateString()} - {comp.finishDate ? new Date(comp.finishDate).toLocaleDateString() : 'Ongoing'}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
